import { useState, useEffect } from 'react';
import { Cog, Clock, CalendarOff, Save, Plus, Trash2, Loader2, Globe, Info, Paperclip, Palette, Upload, X, Database, RefreshCw, CheckCircle, Bell } from 'lucide-react';
import { slaAPI } from '../services/api';
import { api } from '../services/api';
import { toast } from 'sonner';

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function SystemConfigPage() {
  const [tab, setTab] = useState('timezone');
  const [businessHours, setBusinessHours] = useState([]);
  const [bhDraft, setBhDraft] = useState([]);     // local edits — no API until Save
  const [savingBH, setSavingBH] = useState(false);
  const [bhDirty, setBhDirty] = useState(false);  // true when unsaved changes exist
  const [holidays, setHolidays] = useState([]);
  const [slaConfig, setSlaConfig] = useState({ use_business_hours: false });
  const [tenantConfig, setTenantConfig] = useState({ timezone: 'Asia/Kolkata', timezone_choices: [], max_attachments: 3, max_attachment_size_mb: 10, app_name: 'SupportOne', logo_url: null, name: 'TRIVONT' });
  const [loading, setLoading] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [savingTz, setSavingTz] = useState(false);
  const [savingAttach, setSavingAttach] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [attachForm, setAttachForm] = useState({ max_attachments: 3, max_attachment_size_mb: 10 });
  const [brandingForm, setBrandingForm] = useState({ app_name: 'SupportOne' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  // Backup state
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupSchedule, setBackupSchedule] = useState('none');
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', is_recurring: false });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bhRes, hRes, cfgRes, tzRes] = await Promise.allSettled([
        slaAPI.listBusinessHours(),
        slaAPI.listHolidays(),
        slaAPI.getSLAConfig(),
        api.get('/tenants/settings/'),
      ]);
      if (bhRes.status === 'fulfilled') {
        const bh = bhRes.value.data?.results || bhRes.value.data || [];
        setBusinessHours(bh);
        setBhDraft(bh);
        setBhDirty(false);
      }
      if (hRes.status === 'fulfilled') setHolidays(hRes.value.data?.results || hRes.value.data || []);
      if (cfgRes.status === 'fulfilled') setSlaConfig(cfgRes.value.data);
      if (tzRes.status === 'fulfilled') {
        setTenantConfig(tzRes.value.data);
        setAttachForm({
          max_attachments: tzRes.value.data.max_attachments ?? 3,
          max_attachment_size_mb: tzRes.value.data.max_attachment_size_mb ?? 10,
        });
        setBrandingForm({ app_name: tzRes.value.data.app_name || 'SupportOne' });
        if (tzRes.value.data.logo_url) setLogoPreview(tzRes.value.data.logo_url);
      }
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const saveTz = async (tz) => {
    setSavingTz(true);
    try {
      const res = await api.patch('/tenants/settings/', { timezone: tz });
      setTenantConfig(res.data);
      // Update global timezone immediately
      const { setAppTimezone } = await import('../utils/timezone');
      setAppTimezone(tz);
      toast.success(`Timezone set to ${tz}`);
    } catch { toast.error('Failed to update timezone'); }
    finally { setSavingTz(false); }
  };

  const toggleSLAMode = async () => {
    setSavingMode(true);
    const newVal = !slaConfig.use_business_hours;
    try {
      const res = await slaAPI.updateSLAConfig({ use_business_hours: newVal });
      setSlaConfig(res.data);
      toast.success(newVal ? 'SLA mode set to Business Hours' : 'SLA mode set to 24/7 Calendar');
    } catch { toast.error('Failed to update SLA mode'); }
    finally { setSavingMode(false); }
  };

  // Update local draft only — no API call until Save is clicked
  const updateBusinessHour = (bh, field, value) => {
    setBhDraft(prev => prev.map(h => h.id === bh.id ? { ...h, [field]: value } : h));
    setBhDirty(true);
  };

  // Save all business hour changes in one batch → single audit log entry per row
  const saveBusinessHours = async () => {
    setSavingBH(true);
    try {
      const changed = bhDraft.filter(draft => {
        const orig = businessHours.find(h => h.id === draft.id);
        return !orig ||
          orig.is_working_day !== draft.is_working_day ||
          orig.start_time !== draft.start_time ||
          orig.end_time !== draft.end_time;
      });
      await Promise.all(changed.map(bh => slaAPI.updateBusinessHours(bh.id, bh)));
      setBusinessHours(bhDraft);
      setBhDirty(false);
      toast.success('Business hours saved successfully');
    } catch { toast.error('Failed to save business hours'); }
    finally { setSavingBH(false); }
  };

  const [savingHoliday, setSavingHoliday] = useState(false);

  const addHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) { toast.error('Name and date required'); return; }
    setSavingHoliday(true);
    try {
      await slaAPI.createHoliday(newHoliday);
      toast.success('Holiday added');
      setNewHoliday({ name: '', date: '', is_recurring: false });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.name?.[0] || err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed';
      toast.error(detail.includes('already exists') ? `A holiday on this date already exists.` : detail);
    } finally { setSavingHoliday(false); }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete holiday?')) return;
    try { await slaAPI.deleteHoliday(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const fetchBackups = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get('/tenants/backup/list/');
      setBackups(res.data.backups || []);
    } catch { /* ignore */ }
    finally { setBackupLoading(false); }
  };

  const runBackup = async () => {
    setBackupRunning(true);
    try {
      await api.post('/tenants/backup/run/');
      toast.success('Backup completed successfully');
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Backup failed');
    } finally { setBackupRunning(false); }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      let res;
      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        fd.append('app_name', brandingForm.app_name);
        res = await api.patch('/tenants/settings/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setLogoFile(null);
      } else {
        res = await api.patch('/tenants/settings/', { app_name: brandingForm.app_name });
      }
      setTenantConfig(res.data);
      setBrandingForm({ app_name: res.data.app_name || 'SupportOne' });
      if (res.data.logo_url) setLogoPreview(res.data.logo_url);
      toast.success('Branding saved — refresh the page to see the new logo in the sidebar');
    } catch { toast.error('Failed to save branding'); }
    finally { setSavingBranding(false); }
  };

  const removeLogo = async () => {
    setSavingBranding(true);
    try {
      const res = await api.patch('/tenants/settings/', { remove_logo: 'true' });
      setTenantConfig(res.data);
      setLogoPreview(null);
      setLogoFile(null);
      toast.success('Logo removed');
    } catch { toast.error('Failed to remove logo'); }
    finally { setSavingBranding(false); }
  };

  // Load backups when backup tab is selected
  useEffect(() => {
    if (tab === 'backup') fetchBackups();
  }, [tab]); // eslint-disable-line

  const saveAttachmentPolicy = async () => {
    setSavingAttach(true);
    try {
      const res = await api.patch('/tenants/settings/', {
        max_attachments: parseInt(attachForm.max_attachments),
        max_attachment_size_mb: parseInt(attachForm.max_attachment_size_mb),
      });
      setTenantConfig(res.data);
      setAttachForm({ max_attachments: res.data.max_attachments, max_attachment_size_mb: res.data.max_attachment_size_mb });
      toast.success('Attachment policy saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSavingAttach(false); }
  };

  const tabs = [
    { id: 'timezone',    label: 'Timezone',        icon: Globe },
    { id: 'hours',       label: 'Business Hours',  icon: Clock },
    { id: 'holidays',    label: 'Holidays',         icon: CalendarOff },
    { id: 'attachments', label: 'Attachments',      icon: Paperclip },
    { id: 'branding',    label: 'Branding',         icon: Palette },
    { id: 'backup',      label: 'Backup',           icon: Database },
  ];

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const workingDays = businessHours.filter(h => h.is_working_day);

  return (
    <div data-testid="system-config-page">
      <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
        <Cog className="w-6 h-6 text-slate-600" /> System Configuration
      </h2>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl mb-6 w-fit">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-white dark:bg-slate-700 text-sky-700 dark:text-sky-400 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.id}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'timezone' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-sky-500" /> Tenant Timezone
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              All timestamps, SLA deadlines, business hours, and shift calendars are calculated in this timezone.
              Every user in this tenant sees times in this timezone.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block">Select Timezone</label>
                <select
                  value={tenantConfig.timezone || 'Asia/Kolkata'}
                  onChange={e => setTenantConfig(p => ({ ...p, timezone: e.target.value }))}
                  className="w-full border dark:border-slate-700 rounded-xl px-3 py-3 text-sm dark:bg-slate-900 dark:text-white"
                  data-testid="timezone-select">
                  {(tenantConfig.timezone_choices || []).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 mb-3">
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Current time in selected timezone</div>
                  <div className="font-mono text-lg font-bold text-slate-800 dark:text-white" id="tz-clock">
                    {(() => {
                      try {
                        return new Date().toLocaleString('en-IN', {
                          timeZone: tenantConfig.timezone || 'Asia/Kolkata',
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
                        });
                      } catch { return '—'; }
                    })()}
                  </div>
                </div>
                <button onClick={() => saveTz(tenantConfig.timezone)} disabled={savingTz}
                  className="bg-sky-500 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-sky-600 disabled:opacity-50"
                  data-testid="save-timezone-btn">
                  {savingTz ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                  Save Timezone
                </button>
              </div>
            </div>

            <div className="mt-4 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl px-4 py-3 text-xs text-sky-800 dark:text-sky-300 space-y-1">
              <p><strong>What this affects:</strong></p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>SLA due dates calculated using this timezone's business hours</li>
                <li>All ticket timestamps displayed in this timezone</li>
                <li>Shift calendar hours interpreted in this timezone</li>
                <li>Auto-close timer uses this timezone for business day detection</li>
                <li>SLA breach alerts respect this timezone</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {tab === 'hours' && (
        <div className="space-y-5">
          {/* ── SLA Mode Toggle ──────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-teal-600" /> SLA Calculation Mode
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  Controls how SLA due dates are calculated when a new ticket is created.
                </p>
                <div className="flex gap-4">
                  <div className={`flex-1 border-2 rounded-xl p-4 cursor-pointer transition-all ${!slaConfig.use_business_hours ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
                    onClick={() => !slaConfig.use_business_hours || toggleSLAMode()}>
                    <div className="font-semibold text-sm dark:text-white mb-1">24/7 Calendar Time</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">SLA countdown runs continuously — including nights, weekends, holidays.</div>
                    <div className="text-xs text-slate-400 mt-1">Example: P1 = 60 min from now regardless of time</div>
                  </div>
                  <div className={`flex-1 border-2 rounded-xl p-4 cursor-pointer transition-all ${slaConfig.use_business_hours ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
                    onClick={() => slaConfig.use_business_hours || toggleSLAMode()}>
                    <div className="font-semibold text-sm dark:text-white mb-1">Business Hours Only</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">SLA countdown only ticks during configured working hours. Pauses on weekends/holidays.</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {workingDays.length > 0
                        ? `Working: ${workingDays.length} days/week · ${workingDays[0]?.start_time?.slice(0,5)} – ${workingDays[0]?.end_time?.slice(0,5)}`
                        : 'No working days configured below'}
                    </div>
                  </div>
                </div>
                {/* Active mode badge */}
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Current global mode:</span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${slaConfig.use_business_hours ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400'}`}>
                    {slaConfig.use_business_hours ? 'Business Hours Mode' : '24/7 Calendar Mode'}
                  </span>
                  {savingMode && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
              </div>
            </div>
            <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>This global setting applies to all applications. You can override it per-application on the <strong>Applications / Systems</strong> page.</span>
            </div>
          </div>

          {/* ── Business Hours Table ──────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-b dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" /> Working Hours Schedule
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Configure which days and hours count as business hours for SLA calculation</p>
              </div>
              <button onClick={saveBusinessHours} disabled={savingBH || !bhDirty}
                className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                data-testid="save-business-hours-btn">
                {savingBH ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {bhDirty ? 'Save Business Hours' : 'Saved'}
              </button>
            </div>
            {bhDirty && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-6 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                Unsaved changes — click <strong className="mx-1">Save Business Hours</strong> to apply
              </div>
            )}
            <table className="w-full" data-testid="hours-table">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Day</th>
                  <th className="px-4 py-3 text-center font-semibold">Working Day</th>
                  <th className="px-4 py-3 text-center font-semibold">Start Time</th>
                  <th className="px-4 py-3 text-center font-semibold">End Time</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700">
                {dayNames.map((dayName, idx) => {
                  const bh = bhDraft.find(h => h.day_of_week === idx);
                  if (!bh) return null;
                  return (
                    <tr key={idx} className={`text-sm ${!bh.is_working_day ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium dark:text-white">{dayName}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => updateBusinessHour(bh, 'is_working_day', !bh.is_working_day)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${bh.is_working_day ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                          {bh.is_working_day ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input type="time" value={bh.start_time || ''} disabled={!bh.is_working_day}
                          onChange={e => updateBusinessHour(bh, 'start_time', e.target.value)}
                          className="border dark:border-slate-700 rounded-lg px-2 py-1 text-sm dark:bg-slate-900 dark:text-white disabled:opacity-40" />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input type="time" value={bh.end_time || ''} disabled={!bh.is_working_day}
                          onChange={e => updateBusinessHour(bh, 'end_time', e.target.value)}
                          className="border dark:border-slate-700 rounded-lg px-2 py-1 text-sm dark:bg-slate-900 dark:text-white disabled:opacity-40" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'holidays' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm">
          <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-b dark:border-slate-700">
            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2"><CalendarOff className="w-4 h-4 text-rose-500" /> Holidays</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Holidays are skipped in business hours SLA calculations</p>
          </div>
          {/* Add holiday */}
          <div className="p-5 border-b dark:border-slate-700 flex gap-3 flex-wrap">
            <input placeholder="Holiday name" value={newHoliday.name} onChange={e => setNewHoliday({...newHoliday, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white flex-1 min-w-[150px]" />
            <input type="date" value={newHoliday.date} onChange={e => setNewHoliday({...newHoliday, date: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <label className="flex items-center gap-2 text-sm dark:text-slate-300 cursor-pointer">
              <input type="checkbox" checked={newHoliday.is_recurring} onChange={e => setNewHoliday({...newHoliday, is_recurring: e.target.checked})} className="rounded" />
              Recurring yearly
            </label>
            <button onClick={addHoliday} disabled={savingHoliday} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600 disabled:opacity-50">
              {savingHoliday ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Holiday
            </button>
          </div>
          {/* Holiday list */}
          {holidays.length === 0 ? (
            <p className="px-6 py-8 text-center text-slate-400 text-sm">No holidays configured</p>
          ) : (
            <div className="divide-y dark:divide-slate-700">
              {holidays.map(h => (
                <div key={h.id} className="flex items-center justify-between px-6 py-3 text-sm">
                  <div className="flex items-center gap-4">
                    <span className="font-medium dark:text-white">{h.name}</span>
                    <span className="text-slate-500 dark:text-slate-400">{h.date}</span>
                    {h.is_recurring && <span className="text-xs bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 px-2 py-0.5 rounded">Yearly</span>}
                  </div>
                  <button onClick={() => deleteHoliday(h.id)} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === 'attachments' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
              <Paperclip className="w-4 h-4 text-sky-500" /> Attachment Policy
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Control the maximum number of files users can attach per ticket and the maximum size of each file.
              These limits apply to all ticket types (Incidents, Service Requests, Change Requests).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              {/* Max attachments */}
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Max Attachments per Ticket
                </label>
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                  Maximum number of files that can be attached to a single ticket. Range: 1–20.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min={1} max={20}
                    value={attachForm.max_attachments}
                    onChange={e => setAttachForm(p => ({ ...p, max_attachments: e.target.value }))}
                    className="w-24 border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white font-mono text-center text-lg"
                    data-testid="max-attachments-input"
                  />
                  <div className="flex gap-1">
                    {[1, 3, 5, 10].map(v => (
                      <button key={v} onClick={() => setAttachForm(p => ({ ...p, max_attachments: v }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          Number(attachForm.max_attachments) === v
                            ? 'bg-sky-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                        }`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Max file size */}
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Max File Size per Attachment
                </label>
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                  Maximum allowed file size in MB. Range: 1–100 MB.
                </p>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="number" min={1} max={100}
                      value={attachForm.max_attachment_size_mb}
                      onChange={e => setAttachForm(p => ({ ...p, max_attachment_size_mb: e.target.value }))}
                      className="w-24 border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white font-mono text-center text-lg"
                      data-testid="max-attachment-size-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">MB</span>
                  </div>
                  <div className="flex gap-1">
                    {[5, 10, 25, 50].map(v => (
                      <button key={v} onClick={() => setAttachForm(p => ({ ...p, max_attachment_size_mb: v }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          Number(attachForm.max_attachment_size_mb) === v
                            ? 'bg-sky-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                        }`}>
                        {v}MB
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary preview */}
            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-4 mb-6">
              <p className="text-sm text-sky-800 dark:text-sky-300">
                <strong>Current policy:</strong> Users can attach up to{' '}
                <span className="font-bold">{attachForm.max_attachments} file(s)</span> per ticket,
                each up to <span className="font-bold">{attachForm.max_attachment_size_mb} MB</span>.
                Total max storage per ticket:{' '}
                <span className="font-bold">{(attachForm.max_attachments * attachForm.max_attachment_size_mb).toFixed(0)} MB</span>.
              </p>
            </div>

            <button
              onClick={saveAttachmentPolicy}
              disabled={savingAttach}
              className="bg-sky-500 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 disabled:bg-slate-300"
              data-testid="save-attachment-policy-btn"
            >
              {savingAttach ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Attachment Policy
            </button>
          </div>

          {/* Info box */}
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800 dark:text-amber-400 space-y-1">
              <p className="font-semibold">How limits are enforced:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>File size is checked before upload — oversized files are rejected immediately</li>
                <li>Attachment count is checked at time of upload — the {attachForm.max_attachments}th file is the last allowed</li>
                <li>Limits apply to all roles including IT Support and Managers</li>
                <li>Existing attachments on tickets are not affected when you change these settings</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {tab === 'branding' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
              <Palette className="w-4 h-4 text-sky-500" /> Tenant Branding
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Customise the logo and application name shown in the sidebar and login page.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              {/* Logo */}
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">Company Logo</label>
                <p className="text-xs text-slate-400 mb-3">JPEG, PNG, SVG or WebP · Max 2 MB · Wide/banner format recommended (e.g. 300×120px)</p>
                <div className="flex items-start gap-4">
                  {/* Preview — wide banner format matching sidebar */}
                  <div className="w-36 h-14 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-900 flex-shrink-0">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="max-h-12 w-auto max-w-full object-contain p-1" />
                    ) : (
                      <span className="text-2xl font-black text-slate-300 dark:text-slate-600">
                        {(tenantConfig.name || 'T').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 flex-1">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/jpeg,image/png,image/svg+xml,image/webp" className="hidden" onChange={handleLogoSelect} />
                      <span className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-400 px-3 py-2 rounded-xl text-sm hover:bg-sky-100 cursor-pointer">
                        <Upload className="w-4 h-4" /> {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </span>
                    </label>
                    {logoPreview && (
                      <button onClick={removeLogo} className="flex items-center gap-1.5 text-rose-500 hover:text-rose-700 text-xs px-3 py-1.5">
                        <X className="w-3.5 h-3.5" /> Remove logo
                      </button>
                    )}
                    {logoFile && <p className="text-xs text-emerald-600 dark:text-emerald-400">New logo selected — click Save to apply</p>}
                  </div>
                </div>
              </div>

              {/* App name */}
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">Application Name</label>
                <p className="text-xs text-slate-400 mb-3">Shown in the sidebar under your logo and on the login page.</p>
                <input
                  type="text"
                  value={brandingForm.app_name}
                  onChange={e => setBrandingForm(p => ({ ...p, app_name: e.target.value }))}
                  placeholder="e.g. IT Helpdesk"
                  maxLength={100}
                  className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white"
                  data-testid="app-name-input"
                />
                <p className="text-xs text-slate-400 mt-1.5">
                  Preview: <strong className="text-slate-600 dark:text-slate-300">{tenantConfig.name} {brandingForm.app_name}</strong>
                </p>
              </div>
            </div>

            <button onClick={saveBranding} disabled={savingBranding}
              className="bg-sky-500 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 disabled:bg-slate-300"
              data-testid="save-branding-btn">
              {savingBranding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Branding
            </button>
          </div>

          {/* Email Notifications Toggle */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-1">
              <Bell className="w-4 h-4 text-sky-500" /> Email Notifications
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Master switch for all outgoing emails from this tenant (ticket updates, approvals, SLA alerts, team notifications).
              Uses the platform SMTP settings configured in the server <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-xs">.env</code> file.
            </p>
            <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email Notifications
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${tenantConfig.email_notifications_enabled !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                    {tenantConfig.email_notifications_enabled !== false ? 'ENABLED' : 'DISABLED'}
                  </span>
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {tenantConfig.email_notifications_enabled !== false
                    ? 'Emails are being sent for ticket events, approvals and SLA breaches.'
                    : 'All outgoing emails are suppressed for this tenant.'}
                </p>
              </div>
              <button
                onClick={async () => {
                  const newVal = !(tenantConfig.email_notifications_enabled !== false);
                  try {
                    const res = await api.patch('/tenants/settings/', { email_notifications_enabled: newVal });
                    setTenantConfig(res.data);
                    toast.success(`Email notifications ${newVal ? 'enabled' : 'disabled'}`);
                  } catch { toast.error('Failed to update email setting'); }
                }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ml-4 flex-shrink-0 transition-colors ${tenantConfig.email_notifications_enabled !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-200' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 hover:bg-rose-200'}`}>
                <div className={`w-8 h-4 rounded-full relative transition-colors ${tenantConfig.email_notifications_enabled !== false ? 'bg-emerald-500' : 'bg-rose-400'}`}>
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${tenantConfig.email_notifications_enabled !== false ? 'left-4' : 'left-0.5'}`} />
                </div>
                {tenantConfig.email_notifications_enabled !== false ? 'Turn OFF' : 'Turn ON'}
              </button>
            </div>
          </div>

          <div className="bg-sky-50 dark:bg-sky-900/10 border border-sky-200 dark:border-sky-800 rounded-xl p-4 flex gap-3">
            <Info className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-sky-800 dark:text-sky-400">
              After saving, <strong>refresh the page</strong> to see your new logo and app name in the sidebar and login screen.
              The logo is served securely from your server — no external hosting needed.
            </p>
          </div>
        </div>
      )}
      {tab === 'backup' && (
        <div className="space-y-5">
          {/* Header + Run Backup */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                  <Database className="w-4 h-4 text-sky-500" /> Database Backup
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Full backup including <strong>database + uploaded attachments</strong>, stored as <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-xs">.tar.gz</code> at{' '}
                  <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-xs">/opt/itsm/backups/tenant/trivont/</code>
                </p>
              </div>
              <button onClick={runBackup} disabled={backupRunning}
                className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 shadow-lg shadow-sky-500/20"
                data-testid="run-backup-btn">
                {backupRunning
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Running backup...</>
                  : <><Database className="w-4 h-4" /> Run Backup Now</>}
              </button>
            </div>

            {/* Schedule */}
            <div className="border-t dark:border-slate-700 pt-5">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-3">
                Backup Schedule
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { value: 'none', label: 'Manual Only', sub: 'Run on demand' },
                  { value: 'daily', label: 'Daily', sub: 'Every day at midnight' },
                  { value: 'weekly', label: 'Weekly', sub: 'Every Sunday midnight' },
                  { value: 'monthly', label: 'Monthly', sub: '1st of each month' },
                ].map(opt => (
                  <div key={opt.value} onClick={() => setBackupSchedule(opt.value)}
                    className={`cursor-pointer rounded-xl border-2 p-3 transition-all ${
                      backupSchedule === opt.value
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}>
                    <div className={`text-sm font-semibold ${backupSchedule === opt.value ? 'text-sky-700 dark:text-sky-400' : 'text-slate-700 dark:text-slate-300'}`}>
                      {opt.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{opt.sub}</div>
                  </div>
                ))}
              </div>
              {backupSchedule !== 'none' && (
                <div className="mt-3 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-2.5">
                  <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-400">
                    Schedule saved. Automated backups will run as configured. Contact your admin to enable Celery beat for scheduled tasks.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Backup list */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b dark:border-slate-700">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                Backup History
                <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full">{backups.length}</span>
              </h3>
              <button onClick={fetchBackups} disabled={backupLoading}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
                <RefreshCw className={`w-4 h-4 ${backupLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {backupLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-14">
                <Database className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                <p className="text-sm text-slate-500">No backups yet. Click "Run Backup Now" to create your first backup.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b dark:border-slate-700">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Filename</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date & Time</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Size</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {backups.map((b, i) => (
                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="px-5 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">{b.filename}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-400">{b.created_display}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-400">{b.size_display}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                          <CheckCircle className="w-3.5 h-3.5" /> Completed
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-sky-50 dark:bg-sky-900/10 border border-sky-200 dark:border-sky-800 rounded-xl p-4 flex gap-3">
            <Info className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-sky-800 dark:text-sky-400">
              <strong>Backup storage:</strong> Files are stored securely on the server at <code className="bg-sky-100 dark:bg-sky-900 px-1 rounded text-xs">/opt/itsm/backups/tenant/</code>.
              Backups are not downloadable from the browser for security. Contact your system administrator to retrieve backup files.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
